import { createContext, useContext, useState, PropsWithChildren } from 'react';
import { FeatureFlag, FeatureFlagsState } from '@/utils/featureFlags';
import { loadInitialFeatureFlags } from '@/utils/experimentQueryParams';
import { saveFeatureFlags } from '@/utils/featureStorage';
import { setCachedFeatureFlags } from '@/utils/featureEnabled';

type FeatureFlagsContextValue = {
  flags: FeatureFlagsState;
  setFlag: (flag: FeatureFlag, enabled: boolean) => void;
};

export const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(undefined);

export const FeatureFlagsProvider = ({ children }: PropsWithChildren) => {
  const [flags, setFlags] = useState<FeatureFlagsState>(() => {
    const initial = loadInitialFeatureFlags();
    setCachedFeatureFlags(initial);
    return initial;
  });

  const setFlag = (flag: FeatureFlag, enabled: boolean) => {
    setFlags((prev) => {
      const next = { ...prev, [flag]: enabled };
      saveFeatureFlags(next);
      setCachedFeatureFlags(next);
      return next;
    });
  };

  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <FeatureFlagsContext.Provider value={{ flags, setFlag }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export function useFeatureFlagsContext() {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) throw new Error('useFeatureFlagsContext must be used within FeatureFlagsProvider');
  return ctx;
}
